// src/api/ApiClient.tsx
import { OpenAPI } from "./core/OpenAPI";
import axios from "axios";
import { currentSession, stripSessionId } from "./sessionToken";
import { endSession } from "../session/endSession";

const baseUrl = import.meta.env.VITE_API_BASE_URL;

export const configureApiClient = (base: string) => {
  OpenAPI.BASE = base;
};

configureApiClient(baseUrl);

// Axios transport (the generated client) — for every /api call, strip any
// `sessionID` query the thin service modules still build and move the token to
// the `Authorization: Bearer` header. Registered on the default axios instance,
// which is what src/api/core/request.ts uses. This keeps the token out of the
// URL / access log without touching the service call sites.
axios.interceptors.request.use((config) => {
  const url = config.url ?? "";
  if (url.includes("/api/") && !url.includes("/api/login")) {
    const { url: cleanUrl, token } = stripSessionId(url);
    config.url = cleanUrl;
    let sid = token;
    if (config.params && typeof config.params === "object") {
      const p = config.params as Record<string, unknown>;
      if (!sid && typeof p.sessionID === "string") sid = p.sessionID;
      delete p.sessionID;
    }
    sid = sid || currentSession();
    // Auth header (in case a call bypasses the core's OpenAPI.TOKEN path).
    if (sid) {
      const h = config.headers as
        | {
            set?: (k: string, v: string) => void;
            get?: (k: string) => unknown;
            Authorization?: string;
          }
        | undefined;
      if (h && typeof h.set === "function") {
        if (!h.get?.("Authorization")) h.set("Authorization", `Bearer ${sid}`);
      } else {
        config.headers = { ...(config.headers as object), Authorization: `Bearer ${sid}` } as never;
      }
    }
  }
  return config;
});

/**
 * Session expiry: clear the cached session and bounce to /login.
 *
 * 401 means the token was PRESENTED and rejected — unknown or expired. That is
 * the only case that should log the user out. Since 7HAI-923 a request carrying
 * NO token answers 404 (indistinguishable from a path that does not exist, so a
 * scanner learns nothing), and 404 deliberately does not come here: it is a
 * domain answer — "that bid is gone" — and treating it as a logout would sign
 * people out over a stale link.
 *
 * Excluded:
 * - The /api/login endpoint itself — a 401 there is "wrong password", not
 *   "session expired", and the LoginPage handles that state inline.
 * - Non-API calls (asset loads, third-party CDNs).
 *
 * Idempotent: once the path is /login we stop reacting, so the LoginPage's own
 * /api/login 401 doesn't re-bounce.
 */
const handleSessionExpired = (url: string): void => {
  if (!url.includes("/api/") || url.includes("/api/login")) return;
  // 7HAI-1817: one shared implementation (also used by the session watcher);
  // it clears the cached session and lands on /login?reason=expired.
  endSession("expired");
};

// The generated client runs on axios, which in a browser uses the XHR adapter —
// it never touches window.fetch. So the fetch wrapper below could not see those
// responses, and an expired session on any generated-client call left the user
// staring at a failed page instead of the login form (7HAI-923). Both transports
// now report expiry through the same function.
axios.interceptors.response.use(
  (response) => response,
  (error: { response?: { status?: number }; config?: { url?: string } }) => {
    if (error?.response?.status === 401) {
      handleSessionExpired(error.config?.url ?? "");
    }
    return Promise.reject(error);
  },
);

/**
 * Fetch wrapper for the hand-written fetch call sites.
 *
 * Two jobs: attach the session token as a header for any call site that did not
 * (belt and braces — every call site now sends its own), and route 401 to the
 * shared expiry handler.
 */
const installSessionExpiryRedirect = () => {
  if (typeof window === "undefined" || !window.fetch) return;
  const originalFetch = window.fetch.bind(window);

  const extractUrl = (input: RequestInfo | URL): string => {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.toString();
    if (input instanceof Request) return input.url;
    return String(input);
  };

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = extractUrl(input);

    // Since 7HAI-923 the hand-written fetch hooks send `Authorization`
    // themselves and build no `?sessionID=`, so the stripping below should never
    // find anything. It is kept as a net, not as the mechanism: this wrapper is
    // installed from main.tsx and cannot be counted on to have been installed
    // before some future call site's first request, which is exactly the failure
    // that let tokens keep reaching the access log.
    let nextInput = input;
    let nextInit = init;
    if (url.includes("/api/") && !url.includes("/api/login")) {
      const { url: cleanUrl, token } = stripSessionId(url);
      const sid = token || currentSession();
      if (cleanUrl !== url) {
        nextInput = input instanceof Request ? new Request(cleanUrl, input) : cleanUrl;
      }
      if (sid) {
        const headers = new Headers(
          nextInit?.headers ?? (input instanceof Request ? input.headers : undefined),
        );
        if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${sid}`);
        nextInit = { ...(nextInit ?? {}), headers };
      }
    }

    const response = await originalFetch(nextInput, nextInit);
    if (response.status === 401) handleSessionExpired(url);
    return response;
  };
};

installSessionExpiryRedirect();
